<template>
  <div class="flex mx-auto w-full relative mockup-container group/mockup">
    <div
      v-show="!(loading || previewLoading)"
      class="w-full relative"
      :style="{
        backgroundColor: color?.hsl
          ? `hsl(${color?.hsl?.h},100%, 93%)`
          : '#f2f2f2',
      }"
    >
      <div class="mockup-image relative">
        <template v-if="config.background && config.mockup.mask">
          <div
            v-for="(mask, index) in config.mockup.mask"
            :key="`${config.id}-bg-mask-${index}`"
            class="absolute w-full h-full mock-mask"
            :style="{
              backgroundColor: color ? color?.hex : '#f2f2f2',
              // maskImage: `url(${mask.url})`,
            }"
          ></div>
        </template>
        <template v-else>
          <div
            class="absolute w-full h-full"
            :style="{ backgroundColor: color ? color?.hex : '#f2f2f2' }"
          ></div>
        </template>

        <!-- Design -->
        <div
          v-if="config.design"
          :class="getDesignWrapperClass"
          class="absolute w-full h-full overflow-hidden z-0"
        >
          <div
            v-for="(design, index) in config.design"
            :key="`${config.id}-design-mask-${index}`"
            class="w-full h-full mock-mask absolute"
            :style="getDesignContainerStyle(design)"
          >
            <img
              class="absolute"
              fetchpriority="high"
              :class="getDesignClass"
              :style="getDesignStyle(design, config.mockup)"
              :src="data.design"
              alt="Design Image"
            />
          </div>
        </div>

        <!-- Mockup Layer -->
        <div
          class="relative transition-all ease-linear h-full"
          :class="`aspect-[${
            config.mockup.size.width / config.mockup.size.height
          }]`"
        >
          <!-- Preview Image Layer -->
          <img
            v-if="
              config.mockup?.['preview-thumbnail'] &&
              (config.meta?.types?.includes('perspective') ||
                !(
                  !data?.design?.endsWith('png') ||
                  data?.design?.startsWith('blob')
                ))
            "
            :src="config.mockup?.['preview-thumbnail']"
            :alt="config.name"
            fetchpriority="high"
            class="object-fill absolute top-0 left-0 transition-opacity"
            :class="{
              'h-full': isBundle,
              'group-hover/mockup:opacity-0':
                !config.meta?.types?.includes('perspective'),
            }"
            @load="
              () => {
                previewLoading = false;
              }
            "
          />

          <!-- Real Image Preview -->
          <img
            :src="config.mockup.thumbnail || config.mockup.url"
            :alt="config.name"
            fetchpriority="high"
            class="object-fill"
            @load="
              () => {
                loading = false;
              }
            "
          />
        </div>
      </div>

      <div
        v-if="count > 1 && !hideHint && !isSimilar"
        class="z-20 absolute top-2 right-2 rounded-3xl bg-[#18181B29] text-xs md:text-sm font-medium py-0.5 px-2 flex items-center gap-x-1.5 text-white"
      >
        {{ $t("listing.bundle", "Bundle") }}
      </div>

      <div
        class="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 flex gap-x-1 sm:gap-x-2 z-20"
      >
        <!-- <div v-if="background && !isSimilar && !hideHint" class="boxCont">
          <div
            class="box bg-[#18181B29] bg-opacity-50 text-white box-border rounded-full px-2 py-2 flex items-center h-8"
          >
            <span class="whitespace-nowrap overflow-hidden max-w-0 left">
              {{ $t("listing.editableBackground", "Editable Background") }}
            </span>
            <Icon
              icon="solar:mirror-left-outline"
              class="lg:w-4 inline-block img"
            />
          </div>
        </div> -->

        <div v-if="config?.meta?.type === 'overlay'" class="boxCont">
          <div
            class="box bg-[#18181B29] bg-opacity-50 text-white box-border rounded-full px-2.5 py-2.5 flex items-center sm:!h-7 !h-4"
          >
            <span class="whitespace-nowrap overflow-hidden max-w-0 left">
              {{ $t("listing.singleColor", "Single Color") }}
            </span>
            <div
              :style="{ backgroundColor: config?.meta?.type === 'overlay' }"
              class="h-2 sm:h-2.5 w-2 sm:w-2.5 rounded-full inline-block"
            />
          </div>
        </div>
      </div>

      <div
        v-if="config?.pro"
        class="z-20 absolute top-2 right-2 rounded-3xl bg-[#30343B] text-xs font-medium py-0.5 px-2 flex items-center text-white gap-x-0.5"
      >
        <span v-if="isSimilar" class="text-label-sm">PRO</span>
        <span v-else>PRO</span>
        <img
          :src="badgeIcon"
          alt="pro badge"
          :class="isSimilar ? 'w-2.5' : 'w-3'"
        />
      </div>

      <!-- <div v-if="!previewThumbnail && isHoverActive"
        class="absolute h-full w-full top-0 overflow-hidden z-10 bg-black/30 animate-custom">
      </div> -->
    </div>
    <!-- Shimmer -->
    <div
      v-if="loading || previewLoading"
      class="w-full h-full animated-shimmer"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        xmlns:svgjs="http://svgjs.dev/svgjs"
        width="100%"
        height="100%"
        :viewBox="`0 0 ${config.mockup.size.width * renderRatio} ${
          config.mockup.size.height * renderRatio
        }`"
        style=""
      ></svg>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount, onMounted } from "vue";
import { isDesignOnTop, isMobile } from "../../util/common.util";
// import { Icon } from "@iconify/vue";
import badgeIcon from "../../assets/icons/badge.svg";
import { useMockupStore } from "@/datastore/module/mockup.js";

const props = defineProps({
  data: Object,
  config: Object,
  isSimilar: Boolean,
  hideHint: Boolean,
  isBundle: Boolean,
});

const loading = ref(true);
const previewLoading = ref(true);
const renderRatio = ref(1);
const count = ref(0);
const localColor = ref({ hex: "#f2f2f2" });

const store = useMockupStore();

const color = computed(() => localColor.value);
const design = computed(() => props.data.design);
// const background = computed(() => props.config.background);
const bundle = computed(() => store.bundle);

watch(
  () => props.data?.color,
  (newValue) => {
    localColor.value = newValue;
  },
  { deep: true, immediate: true },
);

watch(
  () => props.data?.design,
  (newVal) => {
    design.value = newVal;
  },
);

onBeforeMount(() => {
  if (props.config.mockup?.thumbnail) {
    renderRatio.value = 1 / 4;
  }
  count.value = bundle.value[props.config.bundleId]?.length || 0;
});

onMounted(() => {
  if (
    !props.config.mockup?.["preview-thumbnail"] ||
    !props.data?.design?.endsWith("png") ||
    props.data?.design?.startsWith("blob")
  ) {
    previewLoading.value = false;
  }
});

const getDesignStyle = (design, mockup) => {
  let transformConfig = design.transform;

  let transform = Object.keys(transformConfig)
    .map((d) => {
      let value = transformConfig[d];
      if (Array.isArray(value)) {
        value = transformConfig[d].map((f) => `${f}deg`).join(",");
      } else {
        value = `${transformConfig[d]}deg`;
      }
      return `${d}(${value})`;
    })
    .join(" ");

  let top = (100 * design.position.y) / mockup.size.height;
  let left = (100 * design.position.x) / mockup.size.width;
  let width = (100 * design.size.width) / mockup.size.width;

  if (props.config.meta?.types?.includes("perspective")) {
    width = width / 2;
  }

  return {
    width: width + "%",
    top: `${top}%`,
    left: `${left}%`,
    transform: transform,
  };
};

const getDesignContainerStyle = (design) => {
  let mask;
  if (design.mask) {
    if (design.mask.url) {
      mask = design.mask;
    } else {
      mask = props.config.mockup.mask.find((item) => item.id == design.mask);
    }
  }
  return mask ? { maskImage: `url(${mask.url})` } : {};
};

const getDesignWrapperClass = computed(() => {
  let className = [];
  if (isDesignOnTop(props.config.meta.subcategories)) {
    className.push("!z-10");
  }
  if (props.config?.meta?.type === "overlay") {
    className.push("z-10 mix-blend-multiply");
  }
  return className;
});

const getDesignClass = computed(() => {
  let className = [];
  if (
    props.config.meta.subcategories.includes("sticker") &&
    isDesignOnTop(props.config.meta.subcategories)
  ) {
    className.push(isMobile() ? "sticker-outline-small" : "sticker-outline");
  }
  return className;
});
</script>

<style scoped>
.mock-mask {
  mask-size: contain;
}

.left {
  transition:
    color 0.3s 0.3s,
    max-width 0.3s;
}

.box:hover .left {
  max-width: 180px;
}

.box:hover {
  column-gap: 6px;
  width: 100%;
}

.sticker-outline {
  filter: drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)
    drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)
    drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)
    drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white)
    drop-shadow(0px 0px 0.5px white) drop-shadow(0px 0px 0.5px white);
}

.sticker-outline-small {
  filter: drop-shadow(0px 0px 0.25px white) drop-shadow(0px 0px 0.25px white)
    drop-shadow(0px 0px 0.25px white) drop-shadow(0px 0px 0.25px white)
    drop-shadow(0px 0px 0.25px white) drop-shadow(0px 0px 0.25px white)
    drop-shadow(0px 0px 0.25px white) drop-shadow(0px 0px 0.25px white)
    drop-shadow(0px 0px 0.25px white) drop-shadow(0px 0px 0.25px white);
}

@media screen and (max-width: 640px) {
  .img {
    width: 12px;
  }

  .box {
    height: 20px;
    padding: 4px;
  }

  .left {
    font-size: 12px;
  }

  .box:hover {
    column-gap: 4px;
  }

  .boxCont {
    bottom: 8px;
    right: 8px;
  }
}

.animated-shimmer {
  background: #eeeeee;
  background-image: linear-gradient(
    to right,
    #eeeeee 0%,
    #fafafa 20%,
    #eeeeee 40%,
    #eeeeee 100%
  );
  background-repeat: no-repeat;
  background-size: 300% auto;
  /* display: inline-block; */
  position: relative;
  animation: placeholderShimmer 1.25s ease-out 0s infinite normal forwards;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.animate-custom {
  animation: pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  transition: all;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.thumb-hover {
  transition: all 0.3s;
}

.mockup-container .mockup-image {
  transition: all 0.3s;
}

.mockup-container:hover .mockup-image {
  transform: scale(1.1);
}
</style>
